import { createContext, useCallback, useContext, useMemo } from 'react'

import { toISO8601 } from '@fortem-monorepo/js-utilities'
import { KeyPaths } from '@fortem-monorepo/js-utilities'
import { useFilters } from '@fortem-monorepo/react-spa'
import { ICReport } from '@fortem-monorepo/web-sdk'

import { icReportFilters } from '@/pages/funds/ic-report/filters'
import { SECTIONS } from '@/pages/funds/ic-report/sections'
import { useFunds } from '@/react-query/queries/funds/funds'
import { useICReportTemplateQuery } from '@/react-query/queries/ic-report/hooks'

export const useCreateICStreamContext = () => {
  const { values } = useFilters<typeof icReportFilters>()

  const params = {
    fund_name: values.fund_name,
    st_dt: toISO8601(values.dates?.st_dt!),
    ed_dt: toISO8601(values.dates?.ed_dt ?? values.dates?.st_dt!),
    force_month_end: values.force_month_end,
  }

  // Queries
  const { query: fundsQuery, data: funds } = useFunds()
  const { query: templateQuery, data: template } = useICReportTemplateQuery(params)

  // Variables
  const sidebarLoading = fundsQuery.isLoading || templateQuery.isFetching
  const inputsDisabled = fundsQuery.isFetching || templateQuery.isFetching

  // Memos

  const hasSection = useCallback(
    (section: keyof ICReport.GetICReportTemplateData) => {
      if (!template) return false
      const values = Object.values(template[section])
      return values.some((val) => val)
    },
    [template],
  )

  const hasPrimitive = useCallback(
    (primitive: KeyPaths<ICReport.GetICReportTemplateData, boolean>) => {
      if (!template) return false
      const keys = primitive.split('.')

      let current: any = template
      for (const key of keys) {
        if (current == null || typeof current !== 'object') return false
        current = current[key]
      }

      return typeof current === 'boolean' ? current : false
    },
    [template],
  )

  const filteredSections = useMemo(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        children: section.children
          ? section.children.filter((child) => hasPrimitive(child.key))
          : [],
      })).filter((section) => hasSection(section.key)),
    [template, params.fund_name, params.st_dt, params.ed_dt],
  )

  return {
    fundsQuery,
    templateQuery,
    template,
    params,
    funds,
    sidebarLoading,
    inputsDisabled,
    hasSection,
    hasPrimitive,
    filteredSections,
  }
}

export type ICStreamContext = ReturnType<typeof useCreateICStreamContext>

export const icStreamContext = createContext<ICStreamContext | null>(null)

export const useICStreamContext = () => {
  const context = useContext(icStreamContext)
  if (!context)
    throw new Error('icStreamContext must be used within an ICStreamContext Provider component.')
  return context
}
